import _ from 'lodash';

import { Reference } from '@apollo/client';
import Big from 'big.js';
import * as graphql from 'generated/graphql';
import { ITypeSpecificPolicy } from './shared';



export const ListedCompany: ITypeSpecificPolicy<graphql.ListedCompany> = {
  fields: {
    /**
     * This assumes that the API orders by stock date, in ascending order.
     */
    latestStockPrice: {
      read: (existing, { readField }) => {

        const prices = readField<Reference[]>('historicalStockPrice');

        return _.last(prices);
      }
    },

    averageVolume: {
      read: (existing, { readField }) => {

        const prices = readField<Reference[]>('historicalStockPrice');
        const sum = prices?.reduce((acc, curr) => {
          const value = readField<Big>('volume', curr);
          return acc.plus(value ?? Big(0));
        }, Big(0));

        return sum?.div(prices?.length || 1) ?? Big(0);
      }
    },


    formattedAverageVolume: {
      read: (existing, { readField }) => {
        const val = readField<Big>('averageVolume');
        if (!_.isNil(val)) {
          return val.toNumber().toLocaleString('en-US', {
            useGrouping: true,
            maximumFractionDigits: 0
          });
        }

        return existing;
      }
    },



    percentChangeSinceYesterday: {
      read: (existing, { readField }) => {
        const latestDataPoint = readField<Reference>('latestStockPrice');
        const historicalStockPrices = readField<Reference[]>('historicalStockPrice');
        if ((
          _.isNil(latestDataPoint)
          || _.isNil(historicalStockPrices)
          || _.isEmpty(historicalStockPrices)
        )) {
          return Big(0);
        }

        const yesterdaysDataPoint = historicalStockPrices[historicalStockPrices.length - 2];
        if (_.isNil(yesterdaysDataPoint)) {
          return Big(0);
        }

        const yesterdaysPrice = readField<Big>('value', yesterdaysDataPoint);
        if (_.isNil(yesterdaysPrice) || yesterdaysPrice.eq(0)) {
          return Big(0);
        }

        const latestPrice = readField<Big>('value', latestDataPoint);
        if (_.isNil(latestPrice)) {
          return Big(0);
        }

        return latestPrice.minus(yesterdaysPrice).div(yesterdaysPrice);
      }
    },



    formattedPercentChangeSinceYesterday: {
      read: (existing, { readField }) => {
        const value = readField<Big>('percentChangeSinceYesterday');

        if (!value) {
          return '-';
        }

        return value.mul(100).toFixed(2) + '%';
      }
    },



    standardGlAccounts: {
      merge: (existing, incoming) => {
        if (!existing && incoming) {
          return incoming;
        }

        if (existing && !incoming) {
          return existing;
        }

        if (existing && incoming) {
          return {
            ...existing,
            ...incoming
          };
        }

        return existing;
      }
    },



    stockAnalysis: {
      read: (existing, { readField }) => {
        const ordering: graphql.StockAnalysisSectionCode[] = [
          'businessModel',
          'competitiveAdvantages',
          'growthOpportunities',
          'riskAnalysis',
          'keyVariables',
          'fundamentals',
          'earningsSummary',
          'valuationMarketExpectations',
          'theBet'
        ];

        return _.sortBy(existing, (v) => {
          const field = readField<graphql.StockAnalysisSectionCode>({
            from: v,
            fieldName: 'sectionCode'
          });

          return _.indexOf(ordering, field);
        });
      }
    }
  }
};
